import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CREATE_EXAM_CONFIG,
  ADD_GIFTS_TO_EXAM_CONFIG,
  UPDATE_EXAM_CONFIG,
  UNLINK_GIFTS_FROM_EXAM_CONFIG,
} from '../mutations';
import {
  ExamConfigInput,
  ExamConfigOutput,
  MutationCreateExamConfigArgs,
  MutationAddGiftsToExamConfigArgs,
  GiftsToExamConfigInput,
  MutationUpdateExamConfigArgs,
  MutationUnlinkGiftsFromExamConfigArgs,
  GiftOutput,
  MutationDeleteGiftArgs,
} from '../gql/graphql';
import { graphqlFetch } from '@/utils/graphqlFetch';
import { GET_EXAM_CONFIGS } from '../queries';

export const useGetExamConfigs = () => {
  return useQuery<ExamConfigOutput[], Error>({
    queryKey: ['examConfigs'],
    queryFn: async () => {
      const data = await graphqlFetch<
        { examConfigs: ExamConfigOutput[] },
        null
      >(GET_EXAM_CONFIGS);
      return data.examConfigs;
    },
  });
};

export const useCreateExamConfig = () => {
  const queryClient = useQueryClient();
  return useMutation<ExamConfigOutput, Error, ExamConfigInput>({
    mutationFn: async (input) => {
      const data = await graphqlFetch<
        {
          createExamConfig: ExamConfigOutput;
        },
        MutationCreateExamConfigArgs
      >(CREATE_EXAM_CONFIG, { input });
      return data.createExamConfig;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['examConfigs'] });
    },
  });
};

export const useUpdateExamConfig = () => {
  const queryClient = useQueryClient();
  return useMutation<
    ExamConfigOutput,
    Error,
    { id: string; input: ExamConfigInput }
  >({
    mutationFn: async ({ id, input }) => {
      const data = await graphqlFetch<
        {
          updateExamConfig: ExamConfigOutput;
        },
        MutationUpdateExamConfigArgs
      >(UPDATE_EXAM_CONFIG, { id, input });
      return data.updateExamConfig;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['examConfigs'] });
    },
  });
};

export const useAddGiftsToExamConfig = () => {
  const queryClient = useQueryClient();
  return useMutation<boolean, Error, GiftsToExamConfigInput>({
    mutationFn: async (input) => {
      const data = await graphqlFetch<
        {
          addGiftsToExamConfig: boolean;
        },
        MutationAddGiftsToExamConfigArgs
      >(ADD_GIFTS_TO_EXAM_CONFIG, { input });
      return data.addGiftsToExamConfig;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['examConfigs'] });
    },
  });
};

export const useUnlinkGiftsFromExamConfig = () => {
  const queryClient = useQueryClient();
  return useMutation<
    boolean,
    Error,
    { examConfigId: string; giftIds: string[] }
  >({
    mutationFn: async ({ examConfigId, giftIds }) => {
      const data = await graphqlFetch<
        { unlinkGiftsFromExamConfig: boolean },
        MutationUnlinkGiftsFromExamConfigArgs
      >(UNLINK_GIFTS_FROM_EXAM_CONFIG, {
        input: {
          examConfigId,
          gifts: giftIds,
        },
      });
      return data.unlinkGiftsFromExamConfig;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['examConfigs'] });
    },
  });
};
